import { Block as BlockType } from "../../../models/global";
import { ProcessDataType } from "../../../models/process";
import { FC, useEffect, useRef, useState } from "react";
import { ProcessCircle1, ProcessCircle2, ProcessCircle3, ProcessCircle4, ProcessCircle5, ProcessCircle6, ProcessCircle7, ProcessLine, QuestioMarkWrapper, RoundIndex } from "./styles";
import { Block, Box, Image, MainContainer } from "../../../styles/basicStyles";
import { useTranslation } from "next-i18next";
import { colors } from "../../../styles/colors";
import VisibilitySensor from "react-visibility-sensor";
import ProcessCard from "./processCard";
import Typography from "../../../components/typography";
import Icon from "../../../components/icon";
import Fade from "react-reveal/Fade";

const DATA: ProcessDataType[] = [
  {
    _id: 0,
    icon: 'step_1.png',
    outcomes: ["WEEK_1_OUTCOME_1", "WEEK_1_OUTCOME_2"],
  },
  {
    _id: 1,
    icon: 'step_2.png',
    outcomes: [
      "WEEK_2_3_1_OUTCOME_1",
      "WEEK_2_3_1_OUTCOME_2",
      "WEEK_2_3_1_OUTCOME_3",
      "WEEK_2_3_1_OUTCOME_4",
    ],
  },
  {
    _id: 2,
    icon: 'step_3.png',
    outcomes: ["WEEK_2_3_2_OUTCOME_1"],
  },
  {
    _id: 3,
    icon: 'step_4.png',
    outcomeWithSubtitle: "OUTCOME_WITH_SUBTITLE",
    outcomes: ["WEEK_6_OUTCOME_1"],
  },
  {
    _id: 4,
    icon: 'step_5.png',
    outcomes: ["WEEK_ALL_OUTCOME_1", "WEEK_ALL_OUTCOME_2"],
  },
];

interface OurProcessProps {
  blocks: BlockType[];
}

const OurProcess: FC<OurProcessProps> = ({ blocks }) => {
  const { t, i18n: { language } } = useTranslation();
  const [started, setStarted] = useState<boolean>(false);
  const [onView, setOnView] = useState(0);
  const [offset, setOffset] = useState<number>(0);
  const containerRef = useRef<any>(null);

  useEffect(() => {
    const onScroll = () => {
      setOffset(window.scrollY);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const divisible = containerRef?.current?.clientHeight / 5;

    switch (true) {
      case offset + 800 >
        containerRef?.current?.clientHeight + containerRef?.current?.offsetTop:
        setOnView(0);
        break;
      case offset + 600 > containerRef?.current?.offsetTop + divisible * 4:
        setOnView(5);
        break;
      case offset + 500 > containerRef?.current?.offsetTop + divisible * 3:
        setOnView(4);
        break;
      case offset + 300 > containerRef?.current?.offsetTop + divisible * 2:
        setOnView(3);
        break;
      case offset + 150 > containerRef?.current?.offsetTop + divisible:
        setOnView(2);
        break;
      case offset > containerRef?.current?.offsetTop:
        setOnView(1);
        break;
      case offset < containerRef?.current?.offsetTop:
        setOnView(0);
        break;
      default:
        break;
    }
  }, [offset]);

  const checkState = (isVisible: boolean) => {
    if(isVisible && !started) setStarted(true);
  };

  return (
    <VisibilitySensor onChange={checkState} delayedCall partialVisibility>
      <Box
        pt={{ md: 6.813, xxs: 2.875 }}
        pb={{ md: 12.25, xxs: 6.375 }}
        bgColor={colors.chineseBlack}
        ref={containerRef}
        position='relative'
        overflow='hidden'
      >
        <ProcessCircle1 />
        <ProcessCircle2 />
        <ProcessCircle3 />
        <ProcessCircle4 />
        <ProcessCircle5 />
        <ProcessCircle6 />
        <ProcessCircle7 />
        <MainContainer>
          <Block 
            position='relative'
            zIndex={2}
            w='100%'
          >
            <Box fJustify={{ md: "center", xxs: "flex-start" }}>
              <Typography
                as='h2'
                fSize={{ md: 3.125, xxs: 2 }}
                lHeight={{ md: 3.75, xxs: 3 }}
                fWeight={700}
                color={colors.white}
              >
                {t("WHAT_OUR")}&nbsp;
                <span style={{ position: 'relative' }}>
                  {t("PROCESS").toLowerCase()}
                  <ProcessLine started={started}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="179" height="22" viewBox="0 0 179 22" fill="none">
                      <path d="M2.182 14.9542C20.2261 8.81118 39.8074 5.39798 58.6225 3.81414C62.0189 3.52824 68.3509 1.46872 71.7164 3.36263C77.3868 6.55366 60.4645 15.7207 65.5133 18.7331C67.5258 19.9338 71.8012 18.7865 73.8679 18.4439C80.4351 17.3551 87.0315 15.6693 93.555 14.2429C109.124 10.8387 124.592 7.84424 140.434 6.51069C141.895 6.38766 147.51 4.72187 148.162 7.51649C148.829 10.3765 142.469 12.5668 140.761 13.1827C151.688 10.3015 165.349 12.6376 176.571 12.0463" stroke="#816CFF" strokeWidth="4" strokeLinecap="round"/>
                    </svg>
                  </ProcessLine>
                </span>
                {language === 'en' && ` ${t("LOOKS_LIKE")}`}
                <QuestioMarkWrapper>
                  <Icon noColor icon="outlined_questionMark" />
                </QuestioMarkWrapper>
              </Typography>
            </Box>
            <Block mt={{ md: 5.188, xxs: 3.938 }}>
              {blocks.map((e, index) => (
                <Block key={index}>
                  <Block display={{ md: "flex", xxs: "none" }}>
                    <Box fAlign="center">
                      <Fade left>
                        <Block
                          display={index % 2 === 0 ? "flex" : "none"}
                          mt={1.875}
                          mb={1.875}
                        >
                          <ProcessCard
                            onView={index + 1 === onView ? true : false}
                            index={index + 1}
                            title={e.title[language]}
                            description={e.description[language]}
                            outcomesWithSubtitle={
                              DATA[index].outcomeWithSubtitle
                                ? DATA[index].outcomeWithSubtitle
                                : undefined
                            }
                            outcomes={DATA[index].outcomes}
                          />
                        </Block>
                      </Fade>
                    </Box>
                    <Box
                      w="unset"
                      fJustify="center"
                      fAlign="center"
                      fDirection="column"
                    >
                      <Block
                        h="100%"
                        w={0.125}
                        opacity={index === 0 ? 0 : 0.13}
                        bgColor={index === 0 ? colors.chineseBlack : colors.white}
                      />
                      <Block>
                        <RoundIndex onView={index + 1 === onView ? true : false}>
                          <Image src={`/assets/process/${DATA[index].icon}`} w={{ xs: 1.875, xxs: 1.25 }} h='auto' alt='icon' />
                        </RoundIndex>
                      </Block>
                      <Block
                        h="100%"
                        w={0.125}
                        opacity={index === blocks.length - 1 ? 0 : 0.13}
                        bgColor={
                          index === blocks.length
                            ? colors.chineseBlack
                            : colors.white
                        }
                      />
                    </Box>
                    <Box fAlign="center">
                      <Fade right>
                        <Block
                          display={index % 2 !== 0 ? "flex" : "none"}
                          mt={1.875}
                          mb={1.875}
                        >
                          <ProcessCard
                            onView={index + 1 === onView ? true : false}
                            key={index}
                            index={index + 1}
                            title={e.title[language]}
                            description={e.description[language]}
                            outcomesWithSubtitle={
                              DATA[index].outcomeWithSubtitle
                                ? DATA[index].outcomeWithSubtitle
                                : undefined
                            }
                            outcomes={DATA[index].outcomes}
                          />
                        </Block>
                      </Fade>
                    </Box>
                  </Block>
                  <Block display={{ md: "none", xxs: "flex" }}>
                    <Box
                      w="unset"
                      fJustify="center"
                      fAlign="center"
                      fDirection="column"
                    >
                      <Block display="flex" fAlign="center">
                        <RoundIndex onView={index + 1 === onView ? true : false}>
                          <Image src={`/assets/process/${DATA[index].icon}`} w={{ xs: 1.875, xxs: 1.25 }} h='auto' alt='icon' />
                        </RoundIndex>
                      </Block>
                      <Block
                        h="100%"
                        w={0.125}
                        opacity={index === blocks.length - 1 ? 0 : 0.13}
                        bgColor={colors.white}
                      />
                    </Box>
                    <Box fAlign="center">
                      <Block mt={{ xs: 1.15, xxs: 0.7 }}>
                        <Fade right>
                          <Block
                            mt={{ md: 3.188, xxs: 2.875 }}
                            mb={index !== blocks.length - 1 ? 4.5 : 0}
                          >
                            <ProcessCard
                              onView={index + 1 === onView ? true : false}
                              arrows={false}
                              index={index + 1}
                              title={e.title[language]}
                              description={e.description[language]}
                              outcomesWithSubtitle={
                                DATA[index].outcomeWithSubtitle
                                  ? DATA[index].outcomeWithSubtitle
                                  : undefined
                              }
                              outcomes={DATA[index].outcomes}
                            />
                          </Block>
                        </Fade>
                      </Block>
                    </Box>
                  </Block>
                </Block>
              ))}
            </Block>
          </Block>
        </MainContainer>
      </Box>
    </VisibilitySensor>
  );
};

export default OurProcess;
